import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tenantSubscriptionService } from "@/services/tenant-subscription-service";
import { hotelKeys } from "@/components/pages/hotels/hooks/useHotels";
import { tenantCatalogKeys } from "@/components/pages/tenants/hooks/useTenants";
import { tenantSubscriptionsKeys } from "@/components/pages/tenants/hooks/useTenantSubscriptions";

interface UseHotelSubscriptionOptions {
  refetchInterval?: number | false;
}

export const hotelSubscriptionKeys = {
  all: ["hotelSubscriptions"] as const,
  plans: (tenantId?: string) =>
    [...hotelSubscriptionKeys.all, "plans", tenantId ?? null] as const,
  tenantSubscriptions: () =>
    [...hotelSubscriptionKeys.all, "tenantSubscriptions"] as const,
  tenantTransactions: () =>
    [...hotelSubscriptionKeys.all, "tenantTransactions"] as const,
  tenantTransactionList: (subscriptionId: string) =>
    [...hotelSubscriptionKeys.tenantTransactions(), subscriptionId] as const,
  details: () => [...hotelSubscriptionKeys.all, "detail"] as const,
  /** Every cached plan of one hotel, in own and in tenant mode. */
  hotel: (hotelId: string) =>
    [...hotelSubscriptionKeys.details(), hotelId] as const,
  detail: (hotelId: string, tenantId?: string) =>
    [...hotelSubscriptionKeys.hotel(hotelId), tenantId ?? null] as const,
};

/**
 * After a plan change the hotel plan cells (own and tenant mode), hotel lists
 * and the tenant page plans refresh.
 */
const invalidateHotelPlanViews = (
  queryClient: ReturnType<typeof useQueryClient>,
  hotelId: string,
) => {
  queryClient.invalidateQueries({
    queryKey: hotelSubscriptionKeys.hotel(hotelId),
  });
  queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
  queryClient.invalidateQueries({ queryKey: tenantCatalogKeys.all });
  queryClient.invalidateQueries({ queryKey: tenantSubscriptionsKeys.all });
  queryClient.invalidateQueries({ queryKey: ["userProfile"] });
  queryClient.invalidateQueries({ queryKey: ["userHotel"] });
};

export const useTenantSubscriptionPlans = (
  enabled: boolean,
  tenantId?: string,
) =>
  useQuery({
    queryKey: hotelSubscriptionKeys.plans(tenantId),
    queryFn: () => tenantSubscriptionService.getPlans(tenantId),
    staleTime: 60 * 1000,
    enabled,
  });

export const useMyTenantSubscriptions = (enabled: boolean) =>
  useQuery({
    queryKey: hotelSubscriptionKeys.tenantSubscriptions(),
    queryFn: () => tenantSubscriptionService.getMySubscriptions(),
    staleTime: 60 * 1000,
    enabled,
  });

export const useMySubscriptionTransactions = (
  subscriptionId: string | null,
  enabled: boolean,
) =>
  useQuery({
    queryKey: subscriptionId
      ? hotelSubscriptionKeys.tenantTransactionList(subscriptionId)
      : hotelSubscriptionKeys.tenantTransactions(),
    queryFn: () =>
      tenantSubscriptionService.getMySubscriptionTransactions(
        subscriptionId ?? "",
        {
          limit: 20,
          offset: 0,
        },
      ),
    staleTime: 60 * 1000,
    enabled: enabled && subscriptionId !== null,
  });

export const useHotelSubscription = (
  hotelId: string | null,
  enabled: boolean,
  options?: UseHotelSubscriptionOptions,
  tenantId?: string,
) =>
  useQuery({
    queryKey: hotelId
      ? hotelSubscriptionKeys.detail(hotelId, tenantId)
      : hotelSubscriptionKeys.details(),
    queryFn: () =>
      tenantSubscriptionService.getHotelSubscription(hotelId ?? "", tenantId),
    staleTime: 60 * 1000,
    enabled: enabled && hotelId !== null,
    refetchInterval: options?.refetchInterval ?? false,
  });

export const useAttachHotelSubscriptionPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      hotelId,
      planId,
      roomsNumber,
      tenantId,
    }: {
      hotelId: string;
      planId: string;
      roomsNumber?: number;
      tenantId?: string;
    }) =>
      tenantSubscriptionService.attachHotelPlan(
        hotelId,
        planId,
        roomsNumber,
        tenantId,
      ),
    onSuccess: (_response, variables) => {
      invalidateHotelPlanViews(queryClient, variables.hotelId);
    },
  });
};

export const useSubscriptionPaymentUrl = () =>
  useMutation({
    mutationFn: ({
      subscriptionId,
      returnUrl,
    }: {
      subscriptionId: string;
      returnUrl: string;
    }) => tenantSubscriptionService.getPaymentUrl(subscriptionId, returnUrl),
  });

export const useSubscriptionPortalUrl = () =>
  useMutation({
    mutationFn: (returnUrl: string) =>
      tenantSubscriptionService.getPortalUrl(returnUrl),
  });

export const useDetachHotelSubscriptionPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      hotelId,
      tenantId,
    }: {
      hotelId: string;
      tenantId?: string;
    }) => tenantSubscriptionService.detachHotelPlan(hotelId, tenantId),
    onSuccess: (_response, variables) => {
      invalidateHotelPlanViews(queryClient, variables.hotelId);
    },
  });
};

export const useToggleHotelSubscriptionFeature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      hotelId,
      featureKey,
      enabled,
    }: {
      hotelId: string;
      featureKey: string;
      enabled: boolean;
    }) =>
      tenantSubscriptionService.toggleHotelFeature(
        hotelId,
        featureKey,
        enabled,
      ),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: hotelSubscriptionKeys.hotel(variables.hotelId),
      });
      queryClient.invalidateQueries({
        queryKey: ["selectedHotelSubscription", variables.hotelId],
      });
      queryClient.invalidateQueries({ queryKey: hotelKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["userProfile"] });
      queryClient.invalidateQueries({ queryKey: ["userHotel"] });
    },
  });
};
